import { useCallback, useState, type ChangeEvent } from 'react'
import type { SunoPromptInput, SavedWorldBuilding, SavedCharacter, SavedLyric, SavedSociety } from '@creanizer/types'
import { Button, Label, Input, Textarea } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Loader2, Globe, Users, Music, X, Upload } from 'lucide-react'
import { generateWorldContextText, generateCharacterContext, generateSocietyContext } from '@creanizer/utils'

interface SunoPromptFormProps {
  input: SunoPromptInput
  onChange: (input: SunoPromptInput) => void
  onGenerate: () => void
  isGenerating?: boolean
  savedWorlds?: SavedWorldBuilding[]
  characters?: SavedCharacter[]
  lyrics?: SavedLyric[]
  societies?: SavedSociety[]
  showBatchSettings?: boolean
  onToggleBatchSettings?: () => void
}

export function SunoPromptForm({
  input,
  onChange,
  onGenerate,
  isGenerating = false,
  savedWorlds = [],
  characters = [],
  lyrics = [],
  societies = [],
  showBatchSettings = false,
  onToggleBatchSettings,
}: SunoPromptFormProps) {
  // 選択したIDを管理する状態
  const [selectedWorldId, setSelectedWorldId] = useState<string>('')
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<string[]>([])  // 複数選択対応
  const [selectedLyricId, setSelectedLyricId] = useState<string>('')
  const [selectedSocietyId, setSelectedSocietyId] = useState<string>('')
  const [uploadedFileNames, setUploadedFileNames] = useState<string[]>([])

  const handleChange = useCallback((field: keyof SunoPromptInput, value: any) => {
    onChange({ ...input, [field]: value })
  }, [input, onChange])

  const handleInstrumentsChange = useCallback((value: string) => {
    const instruments = value.split(',').map(i => i.trim()).filter(Boolean)
    handleChange('instruments', instruments)
  }, [handleChange])

  const handleMoodChange = useCallback((value: string) => {
    const mood = value.split(',').map(m => m.trim()).filter(Boolean)
    handleChange('mood', mood)
  }, [handleChange])

  const handleStyleChange = useCallback((value: string) => {
    const style = value.split(',').map(s => s.trim()).filter(Boolean)
    handleChange('style', style)
  }, [handleChange])

  const handleGenerationModeChange = (mode: string) => {
    if (mode === 'batch') {
      onChange({ ...input, batchMode: true, numberOfPrompts: input.numberOfPrompts || 5 })
    } else {
      onChange({ ...input, batchMode: false, numberOfPrompts: undefined })
    }
  }

  const handleFileUpload = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const validFiles: File[] = []
    for (const file of Array.from(files)) {
      if (file.type !== 'text/plain' && !file.name.endsWith('.txt')) {
        continue
      }
      validFiles.push(file)
    }

    if (validFiles.length === 0) {
      alert('テキストファイル(.txt)のみアップロード可能です')
      return
    }

    const contents: string[] = []
    const names: string[] = []
    for (const file of validFiles) {
      const text = await file.text()
      contents.push(`--- ${file.name} ---\n${text}`)
      names.push(file.name)
    }

    setUploadedFileNames(names)
    handleChange('uploadedContent', contents.join('\n\n'))
  }, [handleChange])

  return (
    <>
      <CardHeader>
        <CardTitle>プロンプト設定</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* モード選択（共通設定） */}
        <div className="space-y-2">
          <Label>モード</Label>
          <Select
            value={input.mode}
            onValueChange={(value) => handleChange('mode', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="モードを選択" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="instrumental">インストゥルメンタル</SelectItem>
              <SelectItem value="vocal">ボーカル</SelectItem>
              <SelectItem value="custom">カスタム</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* 生成方式タブ */}
        <Tabs value={input.batchMode ? 'batch' : 'single'} onValueChange={handleGenerationModeChange}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="single">単体生成</TabsTrigger>
            <TabsTrigger value="batch">バッチ生成</TabsTrigger>
          </TabsList>

          <TabsContent value="single" className="space-y-4 mt-4">
            <div className="text-sm text-muted-foreground">
              1つのプロンプトを生成します
            </div>
          </TabsContent>

          <TabsContent value="batch" className="space-y-4 mt-4">
            {/* バッチテーマ */}
            <div className="space-y-2">
              <Label>バッチテーマ</Label>
              <Input
                value={input.batchTheme || ''}
                onChange={(e) => handleChange('batchTheme', e.target.value)}
                placeholder="例: ボス戦バリエーション、街の時間帯別BGM、アルバム収録曲..."
              />
              <p className="text-xs text-muted-foreground">
                生成する曲セットのテーマやコンセプトを指定してください
              </p>
            </div>

            {/* 生成パターン */}
            <div className="space-y-2">
              <Label>生成パターン</Label>
              <Select
                value={input.batchPattern || 'set'}
                onValueChange={(value) => handleChange('batchPattern', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="生成パターンを選択" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="set">セット（関連曲）</SelectItem>
                  <SelectItem value="progression">段階的（徐々に強化）</SelectItem>
                  <SelectItem value="scene">シーン別（異なる場面）</SelectItem>
                  <SelectItem value="emotion">感情別（異なるムード）</SelectItem>
                  <SelectItem value="time">時間帯別（朝昼夜など）</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 生成数 */}
            <div className="space-y-2">
              <Label>生成数: {input.numberOfPrompts || 5}個</Label>
              <Slider
                value={[input.numberOfPrompts || 5]}
                onValueChange={([value]) => handleChange('numberOfPrompts', value)}
                min={2}
                max={20}
                step={1}
              />
              <p className="text-xs text-muted-foreground">
                最大20個まで同時生成可能です（5個ずつ並列処理）
              </p>
            </div>

            {/* シーン説明（batchPatternが'scene'の場合） */}
            {input.batchPattern === 'scene' && (
              <div className="space-y-2">
                <Label>シーン説明（オプション）</Label>
                <Textarea
                  value={input.sceneDescriptions?.join('\n') || ''}
                  onChange={(e) => {
                    const scenes = e.target.value.split('\n').filter(s => s.trim())
                    handleChange('sceneDescriptions', scenes.length > 0 ? scenes : undefined)
                  }}
                  placeholder="各シーンを改行で区切って入力:
ザコ戦
中ボス戦
ラスボス戦
閉じられた扉への挑戦
最終決戦"
                  rows={5}
                />
                <p className="text-xs text-muted-foreground">
                  指定したシーン数と生成数が一致しない場合、AIが自動調整します
                </p>
              </div>
            )}

            {/* 段階説明（batchPatternが'progression'の場合） */}
            {input.batchPattern === 'progression' && (
              <div className="p-3 bg-muted/50 rounded text-sm">
                <p className="font-medium mb-1">段階的パターン</p>
                <p className="text-muted-foreground">
                  音楽が徐々にエピックに、激しくなっていくパターンで生成します。
                  例: 通常戦闘 → 強敵戦 → ボス戦 → ラスボス戦
                </p>
              </div>
            )}

            {/* 感情説明（batchPatternが'emotion'の場合） */}
            {input.batchPattern === 'emotion' && (
              <div className="p-3 bg-muted/50 rounded text-sm">
                <p className="font-medium mb-1">感情別パターン</p>
                <p className="text-muted-foreground">
                  異なる感情やムードを表現する曲を生成します。
                  例: 喜び → 悲しみ → 怒り → 恐怖 → 希望
                </p>
              </div>
            )}

            {/* 時間帯説明（batchPatternが'time'の場合） */}
            {input.batchPattern === 'time' && (
              <div className="p-3 bg-muted/50 rounded text-sm">
                <p className="font-medium mb-1">時間帯別パターン</p>
                <p className="text-muted-foreground">
                  同じ場所の異なる時間帯を表現する曲を生成します。
                  例: 早朝 → 朝 → 昼 → 夕方 → 夜 → 深夜
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* タイトル */}
        <div className="space-y-2">
          <Label>タイトル（オプション）</Label>
          <Input
            value={input.title || ''}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="楽曲のタイトル"
          />
        </div>

        {/* 連携設定 */}
        <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
          <div className="text-sm font-medium mb-2">他ジェネレータとの連携</div>

          {/* 世界観選択 */}
          {savedWorlds.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4" />
                <Label>世界観を選択（オプション）</Label>
              </div>
              <Select
                value={selectedWorldId || 'none'}
                onValueChange={(value) => {
                  if (value === 'none') {
                    setSelectedWorldId('')
                    handleChange('worldContext', undefined)
                  } else {
                    setSelectedWorldId(value)
                    const world = savedWorlds.find(w => w.id === value)
                    if (world) {
                      const worldContext = generateWorldContextText(world.result)
                      handleChange('worldContext', worldContext)
                    }
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="世界観を選択" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">選択しない</SelectItem>
                  {savedWorlds.map((world) => (
                    <SelectItem key={world.id} value={world.id}>
                      {world.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedWorldId && selectedWorldId !== 'none' && (
                <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                  <div className="font-medium text-blue-900 mb-1">選択中の世界観</div>
                  <div className="text-blue-700">
                    {savedWorlds.find(w => w.id === selectedWorldId)?.result.summary?.substring(0, 100)}...
                  </div>
                </div>
              )}
            </div>
          )}

          {/* キャラクター選択（複数選択対応） */}
          {characters.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <Label>キャラクターを選択（複数選択可）</Label>
              </div>

              {/* 選択済みキャラクターの表示 */}
              {selectedCharacterIds.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedCharacterIds.map(id => {
                    const character = characters.find(c => c.id === id)
                    if (!character) return null
                    return (
                      <Badge key={id} variant="secondary" className="flex items-center gap-1">
                        {character.title}
                        <button
                          onClick={() => {
                            const newIds = selectedCharacterIds.filter(cId => cId !== id)
                            setSelectedCharacterIds(newIds)
                            // コンテキストを更新
                            if (newIds.length === 0) {
                              handleChange('characterContext', undefined)
                            } else {
                              const contexts = newIds.map(cId => {
                                const char = characters.find(c => c.id === cId)
                                return char ? generateCharacterContext(char) : ''
                              }).filter(Boolean)
                              handleChange('characterContext', contexts.join('\n\n'))
                            }
                          }}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    )
                  })}
                </div>
              )}

              {/* キャラクターリスト */}
              <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-2">
                {characters.map((character) => (
                  <div key={character.id} className="flex items-start gap-2">
                    <Checkbox
                      id={`char-${character.id}`}
                      checked={selectedCharacterIds.includes(character.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          const newIds = [...selectedCharacterIds, character.id]
                          setSelectedCharacterIds(newIds)
                          // コンテキストを更新
                          const contexts = newIds.map(id => {
                            const char = characters.find(c => c.id === id)
                            return char ? generateCharacterContext(char) : ''
                          }).filter(Boolean)
                          handleChange('characterContext', contexts.join('\n\n'))
                        } else {
                          const newIds = selectedCharacterIds.filter(id => id !== character.id)
                          setSelectedCharacterIds(newIds)
                          // コンテキストを更新
                          if (newIds.length === 0) {
                            handleChange('characterContext', undefined)
                          } else {
                            const contexts = newIds.map(id => {
                              const char = characters.find(c => c.id === id)
                              return char ? generateCharacterContext(char) : ''
                            }).filter(Boolean)
                            handleChange('characterContext', contexts.join('\n\n'))
                          }
                        }
                      }}
                    />
                    <label
                      htmlFor={`char-${character.id}`}
                      className="flex-1 cursor-pointer text-sm"
                    >
                      <div className="font-medium">{character.title}</div>
                      <div className="text-xs text-muted-foreground">
                        {character.result.name} - {character.result.role}
                      </div>
                    </label>
                  </div>
                ))}
              </div>

              {/* 選択中の情報表示 */}
              {selectedCharacterIds.length > 0 && (
                <div className="mt-2 p-2 bg-green-50 rounded text-xs">
                  <div className="font-medium text-green-900 mb-1">
                    選択中のキャラクター ({selectedCharacterIds.length}人)
                  </div>
                  {selectedCharacterIds.length === 2 && (
                    <div className="text-green-700">
                      ※ 2人のキャラクターの関係性をテーマにした曲が生成されます
                    </div>
                  )}
                  {selectedCharacterIds.length > 2 && (
                    <div className="text-green-700">
                      ※ グループやチームをテーマにした曲が生成されます
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ソサエティ選択 */}
          {societies.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <Label>ソサエティを選択（オプション）</Label>
              </div>
              <Select
                value={selectedSocietyId || 'none'}
                onValueChange={(value) => {
                  if (value === 'none') {
                    setSelectedSocietyId('')
                    handleChange('societyContext', undefined)
                  } else {
                    setSelectedSocietyId(value)
                    const society = societies.find(s => s.id === value)
                    if (society) {
                      const societyContext = generateSocietyContext(society)
                      handleChange('societyContext', societyContext)
                    }
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="ソサエティを選択" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">選択しない</SelectItem>
                  {societies.map((society) => (
                    <SelectItem key={society.id} value={society.id}>
                      {society.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedSocietyId && selectedSocietyId !== 'none' && (
                <div className="mt-2 p-2 bg-orange-50 rounded text-xs">
                  <div className="font-medium text-orange-900 mb-1">選択中のソサエティ</div>
                  <div className="text-orange-700">
                    {societies.find(s => s.id === selectedSocietyId)?.title}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 歌詞選択（mode=vocalの場合） */}
          {input.mode === 'vocal' && lyrics.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Music className="h-4 w-4" />
                <Label>歌詞を選択（オプション）</Label>
              </div>
              <Select
                value={selectedLyricId || 'none'}
                onValueChange={(value) => {
                  if (value === 'none') {
                    setSelectedLyricId('')
                    handleChange('useGeneratedLyrics', false)
                    handleChange('lyrics', undefined)
                    handleChange('lyricsWithTags', undefined)
                    handleChange('lyricContext', undefined)
                  } else {
                    setSelectedLyricId(value)
                    const lyric = lyrics.find(l => l.id === value)
                    if (lyric) {
                      // 歌詞テキスト
                      const fullLyrics = lyric.result.content.sections.map(s => s.lyrics.join('\n')).join('\n\n')

                      // タグ付き歌詞
                      const tags = lyric.result.content.sections.map(section => {
                        const tagName = section.type === 'サビ' ? '[chorus]' :
                                       section.type === 'Aメロ' ? '[verse]' :
                                       section.type === 'Bメロ' ? '[pre-chorus]' :
                                       section.type === 'イントロ' ? '[intro]' :
                                       section.type === 'アウトロ' ? '[outro]' :
                                       '[bridge]'
                        return `${tagName}\n${section.lyrics.join('\n')}`
                      }).join('\n\n')

                      // 全ての歌詞情報を構造化して渡す
                      const lyricContext = `
【歌詞情報】
◆基本情報
  タイトル: ${lyric.result.basicInfo.title}
  ジャンル: ${lyric.result.basicInfo.genre}${lyric.result.basicInfo.subGenre ? ` / ${lyric.result.basicInfo.subGenre}` : ''}
  テーマ: ${lyric.result.basicInfo.theme}
  ムード: ${lyric.result.basicInfo.mood}
  言語: ${lyric.result.basicInfo.language}
  コンセプト: ${lyric.result.basicInfo.concept}

◆音楽的情報
  テンポ: ${lyric.result.musicalInfo.tempo}
  拍子: ${lyric.result.musicalInfo.timeSignature}
  ${lyric.result.musicalInfo.rhythmicPattern ? `リズムパターン: ${lyric.result.musicalInfo.rhythmicPattern}\n  ` : ''}
  ${lyric.result.musicalInfo.melodicContour ? `メロディライン: ${lyric.result.musicalInfo.melodicContour}\n  ` : ''}
  ${lyric.result.musicalInfo.vocalRange ? `ボーカルレンジ: ${lyric.result.musicalInfo.vocalRange}\n  ` : ''}
  ${lyric.result.musicalInfo.suggestedKey ? `推奨キー: ${lyric.result.musicalInfo.suggestedKey}\n  ` : ''}
  ダイナミックレンジ: ${lyric.result.musicalInfo.dynamicRange}

◆構成
  フォーマット: ${lyric.result.structure.format}
  セクション数: ${lyric.result.structure.sections.length}

◆分析
  韻律: ${lyric.result.analysis.rhymeAnalysis.scheme} (一貫性: ${lyric.result.analysis.rhymeAnalysis.consistency})
  感情トーン: ${lyric.result.analysis.emotionalTone.primary}${lyric.result.analysis.emotionalTone.secondary ? ` / ${lyric.result.analysis.emotionalTone.secondary}` : ''}
  感情の進行: ${lyric.result.analysis.emotionalTone.progression}
  メインテーマ: ${lyric.result.analysis.themes.main}${lyric.result.analysis.themes.sub ? ` / ${lyric.result.analysis.themes.sub.join('、')}` : ''}
  語彙レベル: ${lyric.result.analysis.vocabulary.level}
  ライティングスタイル: ${lyric.result.analysis.writingStyle}

◆歌詞内容
${tags}
                      `.trim()

                      handleChange('useGeneratedLyrics', true)
                      handleChange('lyrics', fullLyrics)
                      handleChange('lyricsWithTags', tags)
                      // 新しく追加: 全ての歌詞情報を含むコンテキスト
                      handleChange('lyricContext', lyricContext)
                    }
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="歌詞を選択" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">選択しない</SelectItem>
                  {lyrics.map((lyric) => (
                    <SelectItem key={lyric.id} value={lyric.id}>
                      {lyric.result.basicInfo.title || '無題'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedLyricId && selectedLyricId !== 'none' && (
                <div className="mt-2 p-2 bg-purple-50 rounded text-xs">
                  <div className="font-medium text-purple-900 mb-1">選択中の歌詞</div>
                  <div className="text-purple-700">
                    {(() => {
                      const lyric = lyrics.find(l => l.id === selectedLyricId)
                      if (!lyric) return '無題'
                      return (
                        <>
                          <div className="font-medium">{lyric.result.basicInfo.title || '無題'}</div>
                          <div className="text-xs mt-1">
                            {lyric.result.basicInfo.genre} / {lyric.result.basicInfo.mood} /
                            {lyric.result.musicalInfo.tempo}
                          </div>
                        </>
                      )
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* メディアタイプ */}
        <div className="space-y-2">
          <Label>メディアタイプ</Label>
          <Select
            value={input.mediaType || 'game'}
            onValueChange={(value) => handleChange('mediaType', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="メディアタイプを選択" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="game">ゲーム</SelectItem>
              <SelectItem value="anime">アニメ</SelectItem>
              <SelectItem value="movie">映画</SelectItem>
              <SelectItem value="commercial">CM</SelectItem>
              <SelectItem value="podcast">ポッドキャスト</SelectItem>
              <SelectItem value="cd">CD</SelectItem>
              <SelectItem value="live">ライブ</SelectItem>
              <SelectItem value="other">その他</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* ゲームジャンル（mediaType=gameの場合） */}
        {input.mediaType === 'game' && (
          <div className="space-y-2">
            <Label>ゲームジャンル</Label>
            <Select
              value={input.gameGenre || 'rpg'}
              onValueChange={(value) => handleChange('gameGenre', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="ゲームジャンルを選択" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="action">アクション</SelectItem>
                <SelectItem value="rpg">RPG</SelectItem>
                <SelectItem value="simulation">シミュレーション</SelectItem>
                <SelectItem value="strategy">ストラテジー</SelectItem>
                <SelectItem value="adventure">アドベンチャー</SelectItem>
                <SelectItem value="puzzle">パズル</SelectItem>
                <SelectItem value="horror">ホラー</SelectItem>
                <SelectItem value="fighting">格闘</SelectItem>
                <SelectItem value="shooter">シューター</SelectItem>
                <SelectItem value="platformer">プラットフォーマー</SelectItem>
                <SelectItem value="racing">レーシング</SelectItem>
                <SelectItem value="sports">スポーツ</SelectItem>
                <SelectItem value="rhythm">リズム</SelectItem>
                <SelectItem value="visual_novel">ビジュアルノベル</SelectItem>
                <SelectItem value="mmorpg">MMORPG</SelectItem>
                <SelectItem value="custom">カスタム</SelectItem>
              </SelectContent>
            </Select>
            {input.gameGenre === 'custom' && (
              <Input
                value={input.customGameGenre || ''}
                onChange={(e) => handleChange('customGameGenre', e.target.value)}
                placeholder="カスタムゲームジャンルを入力"
                className="mt-2"
              />
            )}
          </div>
        )}

        {/* 世界観設定 */}
        <div className="space-y-2">
          <Label>世界観</Label>
          <Select
            value={input.worldSetting || 'fantasy'}
            onValueChange={(value) => handleChange('worldSetting', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="世界観を選択" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fantasy">ファンタジー</SelectItem>
              <SelectItem value="modern">現代</SelectItem>
              <SelectItem value="sci-fi">SF</SelectItem>
              <SelectItem value="historical">歴史</SelectItem>
              <SelectItem value="post-apocalyptic">ポストアポカリプス</SelectItem>
              <SelectItem value="cyberpunk">サイバーパンク</SelectItem>
              <SelectItem value="steampunk">スチームパンク</SelectItem>
              <SelectItem value="medieval">中世</SelectItem>
              <SelectItem value="japanese">和風</SelectItem>
              <SelectItem value="western">西部劇</SelectItem>
              <SelectItem value="urban">都市</SelectItem>
              <SelectItem value="nature">自然</SelectItem>
              <SelectItem value="space">宇宙</SelectItem>
              <SelectItem value="underwater">海中</SelectItem>
              <SelectItem value="custom">カスタム</SelectItem>
            </SelectContent>
          </Select>
          {input.worldSetting === 'custom' && (
            <Input
              value={input.customWorldSetting || ''}
              onChange={(e) => handleChange('customWorldSetting', e.target.value)}
              placeholder="カスタム世界観を入力"
              className="mt-2"
            />
          )}
        </div>

        {/* 音楽スタイル（複数選択可能） */}
        <div className="space-y-2">
          <Label>音楽スタイル（複数選択可）</Label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3 border rounded-md">
            {[
              { value: 'pop', label: 'ポップス' },
              { value: 'idol', label: 'アイドル' },
              { value: 'rock', label: 'ロック' },
              { value: 'electronic', label: 'エレクトロニック' },
              { value: 'jazz', label: 'ジャズ' },
              { value: 'orchestral', label: 'オーケストラ' },
              { value: 'ambient', label: 'アンビエント' },
              { value: 'chiptune', label: 'チップチューン' },
              { value: 'hybrid', label: 'ハイブリッド' },
              { value: 'acoustic', label: 'アコースティック' },
              { value: 'ethnic', label: 'エスニック' },
              { value: 'experimental', label: 'エクスペリメンタル' },
              { value: 'cinematic', label: 'シネマティック' },
              { value: 'classical', label: 'クラシカル' },
              { value: 'synthwave', label: 'シンセウェーブ' },
              { value: 'metal', label: 'メタル' },
              { value: 'custom', label: 'カスタム' },
            ].map((style) => (
              <div key={style.value} className="flex items-center space-x-2">
                <Checkbox
                  id={`music-${style.value}`}
                  checked={Array.isArray(input.musicStyle) && input.musicStyle.includes(style.value as any) || false}
                  onCheckedChange={(checked) => {
                    const current = Array.isArray(input.musicStyle) ? input.musicStyle : []
                    if (checked) {
                      handleChange('musicStyle', [...current, style.value])
                    } else {
                      handleChange('musicStyle', current.filter(s => s !== style.value))
                    }
                  }}
                />
                <Label htmlFor={`music-${style.value}`} className="text-sm font-normal cursor-pointer">
                  {style.label}
                </Label>
              </div>
            ))}
          </div>
          {Array.isArray(input.musicStyle) && input.musicStyle.includes('custom') && (
            <Input
              value={input.customMusicStyle || ''}
              onChange={(e) => handleChange('customMusicStyle', e.target.value)}
              placeholder="カスタム音楽スタイルを入力"
              className="mt-2"
            />
          )}
          {Array.isArray(input.musicStyle) && input.musicStyle.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {input.musicStyle.filter(s => s !== 'custom').map((style) => (
                <Badge key={style} variant="secondary">{style}</Badge>
              ))}
            </div>
          )}
        </div>

        {/* シーン・用途 */}
        <div className="space-y-2">
          <Label>シーン・用途</Label>
          <Select
            value={input.scene || 'exploration'}
            onValueChange={(value) => handleChange('scene', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="シーンを選択" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="battle">バトル</SelectItem>
              <SelectItem value="boss_battle">ボスバトル</SelectItem>
              <SelectItem value="exploration">探索</SelectItem>
              <SelectItem value="town">街・村</SelectItem>
              <SelectItem value="dungeon">ダンジョン</SelectItem>
              <SelectItem value="emotional">感動</SelectItem>
              <SelectItem value="victory">勝利</SelectItem>
              <SelectItem value="defeat">敗北</SelectItem>
              <SelectItem value="mystery">ミステリー</SelectItem>
              <SelectItem value="chase">チェイス</SelectItem>
              <SelectItem value="relaxation">リラクゼーション</SelectItem>
              <SelectItem value="tension">緊張</SelectItem>
              <SelectItem value="romance">ロマンス</SelectItem>
              <SelectItem value="comedy">コメディ</SelectItem>
              <SelectItem value="epic">エピック</SelectItem>
              <SelectItem value="menu">メニュー</SelectItem>
              <SelectItem value="shop">ショップ</SelectItem>
              <SelectItem value="cutscene">カットシーン</SelectItem>
              <SelectItem value="live">ライブ</SelectItem>
              <SelectItem value="festival">フェスティバル</SelectItem>
              <SelectItem value="concert">コンサート</SelectItem>
              <SelectItem value="recording">レコーディング</SelectItem>
              <SelectItem value="rehearsal">リハーサル</SelectItem>
              <SelectItem value="music_video">ミュージックビデオ</SelectItem>
              <SelectItem value="street_performance">ストリートパフォーマンス</SelectItem>
              <SelectItem value="custom">カスタム</SelectItem>
            </SelectContent>
          </Select>
          {input.scene === 'custom' && (
            <Input
              value={input.customScene || ''}
              onChange={(e) => handleChange('customScene', e.target.value)}
              placeholder="カスタムシーンを入力"
              className="mt-2"
            />
          )}
        </div>

        {/* 音楽的要素タブ */}
        <Tabs defaultValue="tempo" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="tempo">テンポ</TabsTrigger>
            <TabsTrigger value="instruments">楽器</TabsTrigger>
            <TabsTrigger value="mood">ムード</TabsTrigger>
          </TabsList>

          <TabsContent value="tempo" className="space-y-4">
            <div className="space-y-2">
              <Label>テンポ</Label>
              <Select
                value={input.tempo || 'moderate'}
                onValueChange={(value) => handleChange('tempo', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="slow">スロー (60-90 BPM)</SelectItem>
                  <SelectItem value="moderate">モデレート (90-120 BPM)</SelectItem>
                  <SelectItem value="fast">ファスト (120-180 BPM)</SelectItem>
                  <SelectItem value="variable">可変</SelectItem>
                  <SelectItem value="specific">BPM指定</SelectItem>
                </SelectContent>
              </Select>
              {input.tempo === 'specific' && (
                <div className="space-y-2 mt-4">
                  <Label>BPM: {input.specificBpm || 120}</Label>
                  <Slider
                    value={[input.specificBpm || 120]}
                    onValueChange={(value) => handleChange('specificBpm', value[0])}
                    min={60}
                    max={200}
                    step={5}
                  />
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="instruments" className="space-y-4">
            <div className="space-y-2">
              <Label>使用楽器（カンマ区切り）</Label>
              <Textarea
                value={input.instruments?.join(', ') || ''}
                onChange={(e) => handleInstrumentsChange(e.target.value)}
                placeholder="例: piano, strings, synthesizer, drums"
                rows={3}
              />
            </div>
          </TabsContent>

          <TabsContent value="mood" className="space-y-4">
            <div className="space-y-2">
              <Label>ムード（カンマ区切り）</Label>
              <Textarea
                value={input.mood?.join(', ') || ''}
                onChange={(e) => handleMoodChange(e.target.value)}
                placeholder="例: uplifting, energetic, mysterious"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>スタイル（カンマ区切り）</Label>
              <Textarea
                value={input.style?.join(', ') || ''}
                onChange={(e) => handleStyleChange(e.target.value)}
                placeholder="例: modern, retro, experimental"
                rows={2}
              />
            </div>
          </TabsContent>
        </Tabs>

        {/* ボーカル設定（ボーカルモードの場合） */}
        {input.mode === 'vocal' && (
          <div className="space-y-4 p-4 border rounded-lg">
            <h3 className="font-semibold">ボーカル設定</h3>

            <div className="space-y-2">
              <Label>ボーカルスタイル</Label>
              <Select
                value={input.vocalStyle || 'female'}
                onValueChange={(value) => handleChange('vocalStyle', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">男性</SelectItem>
                  <SelectItem value="female">女性</SelectItem>
                  <SelectItem value="duet">デュエット</SelectItem>
                  <SelectItem value="chorus">コーラス</SelectItem>
                  <SelectItem value="child">子供</SelectItem>
                  <SelectItem value="mixed">ミックス</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>言語</Label>
              <Select
                value={input.language || 'english'}
                onValueChange={(value) => handleChange('language', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="english">英語</SelectItem>
                  <SelectItem value="japanese">日本語</SelectItem>
                  <SelectItem value="spanish">スペイン語</SelectItem>
                  <SelectItem value="french">フランス語</SelectItem>
                  <SelectItem value="other">その他</SelectItem>
                </SelectContent>
              </Select>
              {input.language === 'other' && (
                <Input
                  value={input.customLanguage || ''}
                  onChange={(e) => handleChange('customLanguage', e.target.value)}
                  placeholder="言語を入力"
                  className="mt-2"
                />
              )}
            </div>

            <div className="space-y-2">
              <Label>歌詞</Label>
              <Textarea
                value={input.lyrics || ''}
                onChange={(e) => handleChange('lyrics', e.target.value)}
                placeholder="歌詞を入力（タグなし）"
                rows={6}
              />
            </div>

            <div className="space-y-2">
              <Label>タグ付き歌詞（オプション）</Label>
              <Textarea
                value={input.lyricsWithTags || ''}
                onChange={(e) => handleChange('lyricsWithTags', e.target.value)}
                placeholder="[intro], [verse], [chorus]などのタグ付き歌詞"
                rows={6}
              />
            </div>
          </div>
        )}

        {/* 詳細設定 */}
        <div className="space-y-4 p-4 border rounded-lg">
          <h3 className="font-semibold">詳細設定</h3>

          <div className="space-y-2">
            <Label>プロンプト長</Label>
            <Select
              value={input.promptLength || 'medium'}
              onValueChange={(value) => handleChange('promptLength', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="short">短い (300-400文字)</SelectItem>
                <SelectItem value="medium">標準 (500-700文字)</SelectItem>
                <SelectItem value="long">長い (800-1000文字)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>創造性レベル: {input.creativityLevel || 7}</Label>
              <Badge variant="outline">{input.creativityLevel || 7}/10</Badge>
            </div>
            <Slider
              value={[input.creativityLevel || 7]}
              onValueChange={(value) => handleChange('creativityLevel', value[0])}
              min={1}
              max={10}
              step={1}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>技術的詳細度: {input.technicalDetail || 7}</Label>
              <Badge variant="outline">{input.technicalDetail || 7}/10</Badge>
            </div>
            <Slider
              value={[input.technicalDetail || 7]}
              onValueChange={(value) => handleChange('technicalDetail', value[0])}
              min={1}
              max={10}
              step={1}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="negative-prompt"
              checked={input.includeNegativePrompt || false}
              onCheckedChange={(checked) => handleChange('includeNegativePrompt', checked)}
            />
            <Label htmlFor="negative-prompt">ネガティブプロンプトも生成</Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="pro-mode"
              checked={input.useProMode || false}
              onCheckedChange={(checked) => handleChange('useProMode', checked)}
            />
            <Label htmlFor="pro-mode">Proモード（高精度）</Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="gemini3-mode"
              checked={input.useGemini3Mode || false}
              onCheckedChange={(checked) => handleChange('useGemini3Mode', checked)}
            />
            <Label htmlFor="gemini3-mode">Gemini 3 モード（有料APIのみ・試験版）</Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="gemini3-flash-mode"
              checked={input.useGemini3FlashMode || false}
              onCheckedChange={(checked) => handleChange('useGemini3FlashMode', checked)}
            />
            <Label htmlFor="gemini3-flash-mode">Gemini 3 Flash モード（有料APIのみ・試験版・高速）</Label>
          </div>
        </div>

        {/* ファイルアップロード */}
        <div className="space-y-2">
          <Label>参考資料（テキストファイル・複数選択可）</Label>
          <Button
            type="button"
            variant="outline"
            onClick={() => document.getElementById('file-upload-suno')?.click()}
            className="w-full"
          >
            <Upload className="mr-2 h-4 w-4" />
            {uploadedFileNames.length > 0
              ? `${uploadedFileNames.length}件のファイルを選択中`
              : 'テキストファイルをアップロード (.txt)'}
          </Button>
          <input
            id="file-upload-suno"
            type="file"
            accept=".txt"
            onChange={handleFileUpload}
            className="hidden"
            multiple
          />
          {uploadedFileNames.length > 0 && (
            <div className="space-y-1">
              {uploadedFileNames.map((name, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-sm text-muted-foreground truncate">
                    {name}
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between p-2">
                <span className="text-xs text-muted-foreground">
                  合計 {input.uploadedContent?.length || 0}文字
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setUploadedFileNames([])
                    handleChange('uploadedContent', undefined)
                  }}
                >
                  <X className="h-4 w-4" />
                  すべて削除
                </Button>
              </div>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            小説、設定資料などのテキストファイルから音楽のイメージを抽出します
          </p>
        </div>

        {/* 追加指示 */}
        <div className="space-y-2">
          <Label htmlFor="additional-settings">追加指示（自由記述）</Label>
          <Textarea
            id="additional-settings"
            placeholder="生成に関する追加の指示があれば入力してください..."
            value={input.additionalSettings || ''}
            onChange={(e) => handleChange('additionalSettings', e.target.value)}
            className="min-h-[80px]"
          />
        </div>

        {/* 生成ボタン */}
        <Button
          onClick={onGenerate}
          disabled={isGenerating || !input.mode}
          className="w-full"
        >
          {isGenerating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              生成中...
            </>
          ) : (
            'プロンプトを生成'
          )}
        </Button>
      </CardContent>
    </>
  )
}